import React, { useEffect, useState } from 'react'
import { FaEdit, FaTrash } from 'react-icons/fa'
import Breadcrum from '../../components/breadcrum'
import AddItem from '../../components/addItem'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { allSample, deleteSample, sendMail } from '../../api/sampleApi'
import { Link } from 'react-router-dom'
import { getCleanPdfName, handleDate } from '../../handleDateAndPdf'
import Swal from 'sweetalert2'

export default function SampleReport() {
  const queryClient = useQueryClient()
  const [toDelete, setToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)

  useEffect(() => {
    document.title = 'Sample Report'
  }, [])

  const {
    data: reports = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['sample'],
    queryFn: allSample,
    onError: (error) => {
      toast.error('Error fetching reports:', error)
    },
  })

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => sendMail(id),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) => deleteSample(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch samples
      queryClient.invalidateQueries({ queryKey: ['sample'] })

      // Show success message
      toast.success('sample deleted successfully!')

      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Deleted!',
        text: 'The sample has been deleted successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setToDelete(null)
    },
    onError: (err) => {
      console.error('Delete error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete sample. Please check your OTP.'
      toast.error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
        <p>Please enter the OTP sent to your email</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
          <small style="color: #1e40af;">
            <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
          </small>
        </div>
      `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Delete sample',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Deleting...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: toDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or deletion failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setToDelete(null)
          toast.info('Deletion cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (sampleId, sampleTitle) => {
    setToDelete(sampleId)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Delete sample?',
      html: `
        <p>Are you sure you want to delete this sample?</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
          <strong style="color: #dc2626;">Sample ${sampleTitle}</strong>
        </div>
        <p style="color: #6b7280; font-size: 14px;">
          This action will send an OTP to your email for verification.
        </p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(sampleId)
      } else {
        setToDelete(null)
      }
    })
  }

  if (isLoading) {
    return (
      <>
        <Breadcrum heading="DSJ Insight" subheading="Sample Report" />
        <div className="text-center py-8">Loading reports...</div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Breadcrum heading="DSJ Insight" subheading="Sample Report" />
        <div className="text-center py-8 text-red-500">
          Error loading trending news. Please try again later.
        </div>
      </>
    )
  }

  return (
    <>
      <Breadcrum subheading="Sample Report" heading="DSJ Insight" />
      <div className="p-6 max-w-6xl mx-auto bg-white rounded shadow">
        <h2 className="text-2xl font-semibold mb-4">Uploaded Files</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 text-sm text-left">
            <thead className="bg-gray-100 text-gray-700 font-semibold">
              <tr>
                <th className="px-4 py-3 border-b">Title</th>
                <th className="px-4 py-3 border-b">Report</th>
                <th className="px-4 py-3 border-b">CreatedAt</th>
                <th className="px-4 py-3 border-b">CreatedBy</th>
                <th className="px-4 py-3 border-b">UpdatedAt</th>
                <th className="px-4 py-3 border-b">UpdatedBy</th>
                <th className="px-4 py-3 border-b text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report, index) => (
                <tr key={index} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">{report.title}</td>
                  <td className="px-4 py-3">{getCleanPdfName(report.pdf)}</td>
                  <td className="px-4 py-3">{handleDate(report.createdAt)}</td>
                  <td className="px-4 py-3">{report?.createAdmin}</td>
                  <td className="px-4 py-3">{handleDate(report.updatedAt)}</td>
                  <td className="px-4 py-3">{report?.updateAdmin}</td>
                  <td className="px-4 py-3 text-center flex justify-center gap-2">
                    <Link
                      to={`/dealdashboard/add/sample/${report.id}`}
                      className="bg-yellow-400 hover:bg-yellow-500 text-white p-2 rounded"
                    >
                      <FaEdit size={16} />
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDeleteClick(report.id, report.title)}
                      className={`text-red-500 hover:text-red-700 transition-colors p-1 ${
                        sendingMail || deleteMutation.isPending
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                      title="Delete Slider"
                      disabled={sendingMail || deleteMutation.isPending}
                    >
                      {sendingMail && toDelete === report.id ? (
                        <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <FaTrash />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Loading overlay for sending email */}
        {sendingMail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg font-semibold">
                Sending verification email...
              </p>
              <p className="text-sm text-gray-500 mt-1">Please wait</p>
            </div>
          </div>
        )}
      </div>
      <AddItem redirect="/dealdashboard/add/sample" />
    </>
  )
}
